import { useState, useMemo, useCallback } from 'react';
import { useCompanyActivities, useProjectRequests } from '../hooks';
import { ActivityFormModal } from '../activities/ActivityFormModal';
import { RichTextDisplay } from '../../components/RichTextEditor';
import type { Activity, ActivityType } from '../types';
import styles from './CompanyDetailPage.module.css';

// ============================================================
//  Constants
// ============================================================

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  NOTE: 'Notiz',
  CALL: 'Anruf',
  EMAIL: 'E-Mail',
  MEETING: 'Meeting',
  TASK: 'Aufgabe',
  FOLLOWUP: 'Follow-Up',
  APPOINTMENT: 'Termin',
  SOCIAL_MEDIA: 'Social Media',
  MESSENGER: 'Messenger',
  EVENT: 'Event – Teilnahme',
};

const iconSvgProps = {
  width: 14,
  height: 14,
  stroke: 'currentColor',
  fill: 'none',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function ActivityIcon({ type }: { type: ActivityType }) {
  switch (type) {
    case 'NOTE':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <rect x="2" y="2" width="12" height="12" rx="1" />
          <path d="M5 5h6M5 8h6M5 11h3" />
        </svg>
      );
    case 'CALL':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <path d="M3 2h3l1.5 3L6 6.5c.7 1.4 2.1 2.8 3.5 3.5L11 8.5l3 1.5v3a1 1 0 01-1 1C7 14 2 9 2 3a1 1 0 011-1z" />
        </svg>
      );
    case 'EMAIL':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <rect x="1" y="3" width="14" height="10" rx="1" />
          <path d="M1 4l7 5 7-5" />
        </svg>
      );
    case 'MEETING':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <circle cx="5" cy="5" r="2" />
          <circle cx="11" cy="5" r="2" />
          <path d="M1 12c0-2 2-3 4-3s4 1 4 3M7 12c0-2 2-3 4-3s4 1 4 3" />
        </svg>
      );
    case 'TASK':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <rect x="2" y="2" width="12" height="12" rx="1" />
          <path d="M5 8l2 2 4-4" />
        </svg>
      );
    case 'APPOINTMENT':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <rect x="1" y="3" width="14" height="12" rx="1" />
          <path d="M5 1v4M11 1v4M1 7h14" />
        </svg>
      );
    case 'SOCIAL_MEDIA':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <circle cx="12" cy="3" r="1.5" />
          <circle cx="4" cy="8" r="1.5" />
          <circle cx="12" cy="13" r="1.5" />
          <path d="M5.5 7.2l5-3M5.5 8.8l5 3" />
        </svg>
      );
    case 'MESSENGER':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <path d="M2 2h12a1 1 0 011 1v8a1 1 0 01-1 1H5l-3 2V3a1 1 0 011-1z" />
        </svg>
      );
    case 'EVENT':
      return (
        <svg {...iconSvgProps} viewBox="0 0 16 16">
          <path d="M8 1l1.8 3.6L14 5.4l-3 2.9.7 4.1L8 10.4l-3.7 2 .7-4.1-3-2.9 4.2-.8z" />
        </svg>
      );
    default:
      return null;
  }
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function contactDisplayName(a: Activity): string | null {
  if (!a.contact) return null;
  const parts = [a.contact.firstName, a.contact.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : a.contact.companyName ?? null;
}

function toDateStr(iso: string): string {
  return iso.substring(0, 10);
}

// ============================================================
//  Filter bar styles
// ============================================================

const filterBarStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.5rem',
  flexWrap: 'wrap',
  alignItems: 'center',
  marginBottom: '1rem',
  padding: '0.5rem 0.75rem',
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
};

const filterInputStyle: React.CSSProperties = {
  padding: '0.3rem 0.5rem',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.8125rem',
  background: 'var(--color-bg-card)',
  color: 'var(--color-text)',
  outline: 'none',
};

// ============================================================
//  Component
// ============================================================

interface ActivityFeedProps {
  companyId: string;
}

export function ActivityFeed({ companyId }: ActivityFeedProps) {
  const { data, isLoading } = useCompanyActivities(companyId);
  const { data: prData } = useProjectRequests(companyId);

  const allActivities: Activity[] = data?.data ?? [];
  const projectRequests = prData?.data ?? [];

  // Filter state
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<ActivityType | ''>('');
  const [projectFilter, setProjectFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [isFormOpen, setFormOpen] = useState(false);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Client-side filtering
  const activities = useMemo(() => {
    let filtered = allActivities;

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.subject.toLowerCase().includes(q) ||
          (a.description ?? '').toLowerCase().includes(q) ||
          (contactDisplayName(a) ?? '').toLowerCase().includes(q),
      );
    }

    if (typeFilter) {
      filtered = filtered.filter((a) => a.type === typeFilter);
    }

    if (projectFilter) {
      filtered = filtered.filter((a) => a.projectRequestId === projectFilter);
    }

    if (dateFrom) {
      filtered = filtered.filter((a) => toDateStr(a.scheduledAt ?? a.createdAt) >= dateFrom);
    }

    if (dateTo) {
      filtered = filtered.filter((a) => toDateStr(a.scheduledAt ?? a.createdAt) <= dateTo);
    }

    return filtered;
  }, [allActivities, search, typeFilter, projectFilter, dateFrom, dateTo]);

  return (
    <div className={styles.card}>
      {/* Header with CTA */}
      <div className={styles.cardTitleRow}>
        <h2 className={styles.cardTitle} style={{ marginBottom: 0 }}>
          Kontakthistorie{' '}
          <span style={{ fontWeight: 400, fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
            ({activities.length})
          </span>
        </h2>
        <button
          onClick={() => setFormOpen(true)}
          style={{
            padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
            background: 'var(--color-primary)', color: 'white',
            border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          + Neue Aktivität
        </button>
      </div>

      {/* Filter bar — compact single-row box */}
      <div style={filterBarStyle}>
        <input
          type="search"
          placeholder="Durchsuchen..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...filterInputStyle, flex: '1 1 140px', minWidth: 100 }}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ActivityType | '')}
          style={filterInputStyle}
        >
          <option value="">Alle Typen</option>
          {(Object.keys(ACTIVITY_TYPE_LABELS) as ActivityType[]).map((t) => (
            <option key={t} value={t}>{ACTIVITY_TYPE_LABELS[t]}</option>
          ))}
        </select>
        {projectRequests.length > 0 && (
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            style={{ ...filterInputStyle, maxWidth: 180 }}
          >
            <option value="">Alle Projekte</option>
            {projectRequests.map((pr) => (
              <option key={pr.id} value={pr.id}>{pr.title}</option>
            ))}
          </select>
        )}
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{ ...filterInputStyle, width: 120 }}
          title="Von"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{ ...filterInputStyle, width: 120 }}
          title="Bis"
        />
        {(search || typeFilter || projectFilter || dateFrom || dateTo) && (
          <button
            onClick={() => { setSearch(''); setTypeFilter(''); setProjectFilter(''); setDateFrom(''); setDateTo(''); }}
            style={{
              padding: '0.25rem 0.5rem', fontSize: '0.75rem',
              background: 'transparent', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              color: 'var(--color-text-muted)',
            }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Activity List */}
      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.875rem' }}>
          Laden...
        </p>
      ) : activities.length === 0 ? (
        <div className={styles.feedEmpty}>
          {allActivities.length > 0
            ? 'Keine Einträge für die gewählten Filter'
            : 'Noch keine Einträge vorhanden'}
        </div>
      ) : (
        <div className={styles.feedList}>
          {activities.map((activity) => {
            const viaName = contactDisplayName(activity);
            const isExpanded = expandedIds.has(activity.id);
            const hasDetails = !!(activity.description);

            const iconClass = {
              NOTE: styles.feedIconNote,
              CALL: styles.feedIconCall,
              EMAIL: styles.feedIconEmail,
              MEETING: styles.feedIconMeeting,
              TASK: styles.feedIconTask,
              FOLLOWUP: styles.feedIconFollowup,
              APPOINTMENT: styles.feedIconAppointment,
              SOCIAL_MEDIA: styles.feedIconSocialMedia,
              MESSENGER: styles.feedIconMessenger,
              EVENT: styles.feedIconEvent,
            }[activity.type] ?? '';

            const badgeClass = {
              NOTE: styles.feedBadgeNote,
              CALL: styles.feedBadgeCall,
              EMAIL: styles.feedBadgeEmail,
              MEETING: styles.feedBadgeMeeting,
              TASK: styles.feedBadgeTask,
              FOLLOWUP: styles.feedBadgeFollowup,
              APPOINTMENT: styles.feedBadgeAppointment,
              SOCIAL_MEDIA: styles.feedBadgeSocialMedia,
              MESSENGER: styles.feedBadgeMessenger,
              EVENT: styles.feedBadgeEvent,
            }[activity.type] ?? '';

            return (
              <div
                key={activity.id}
                className={styles.feedItem}
                onClick={() => toggleExpand(activity.id)}
              >
                <div className={`${styles.feedIcon} ${iconClass}`}>
                  <ActivityIcon type={activity.type} />
                </div>
                <div className={styles.feedContent}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className={styles.feedSubject}>{activity.subject}</div>
                    {hasDetails && (
                      <svg
                        width="12" height="12" viewBox="0 0 12 12"
                        fill="none" stroke="currentColor" strokeWidth="1.75"
                        strokeLinecap="round" strokeLinejoin="round"
                        style={{
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.15s',
                          color: 'var(--color-text-muted)',
                          flexShrink: 0,
                          marginTop: '0.3rem',
                        }}
                      >
                        <path d="M4 2l4 4-4 4" />
                      </svg>
                    )}
                  </div>
                  <div className={styles.feedMeta}>
                    <span className={`${styles.feedTypeBadge} ${badgeClass}`}>
                      {ACTIVITY_TYPE_LABELS[activity.type]}
                    </span>
                    <span className={styles.feedDate}>{formatDateTime(activity.scheduledAt ?? activity.createdAt)}</span>
                    {viaName && activity.contactId && (
                      <span className={styles.feedViaBadge}>
                        {viaName}
                      </span>
                    )}
                    {activity.projectRequest && (
                      <span style={{
                        fontSize: '0.6875rem', fontWeight: 500,
                        padding: '0.0625rem 0.375rem', borderRadius: '999px',
                        background: activity.projectRequest.status?.color ? `${activity.projectRequest.status.color}20` : '#ede9fe',
                        color: activity.projectRequest.status?.color ?? '#5b21b6',
                        border: `1px solid ${activity.projectRequest.status?.color ?? '#5b21b6'}30`,
                      }}>
                        {activity.projectRequest.title}
                      </span>
                    )}
                  </div>
                  {isExpanded && activity.description && (
                    <RichTextDisplay
                      html={activity.description}
                      className={styles.feedDesc}
                      style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ActivityFormModal
        isOpen={isFormOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => setFormOpen(false)}
        companyId={companyId}
      />
    </div>
  );
}
